import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Events } from 'ionic-angular';
import { EVENTS_NAME } from '../../providers/myria/app-variables';
import { InternalIssueReportProvider } from '../../providers/internal-issue-report/internal-issue-report';
import {
  INTERNAL_ISSUE_CATEGORIES,
  INTERNAL_ISSUE_SEVERITIES,
  INTERNAL_ISSUE_TEXT_MAX_LENGTH,
  InternalIssueCategory,
  InternalIssueSeverity,
} from '../../model/types/internal-issue-report-type';
import { RmtSnackbarComponent } from '../rmt-snackbar/rmt-snackbar';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';

/**
 * Floating "report an issue" button plus bottom sheet for internal testers.
 * Mounted once from app.html and hoisted onto ion-app so it floats over every page,
 * modal and RMT5 screen. Only visible while the internal reporter feature flag is on.
 */
@Component({
  selector: 'rmt-internal-issue-reporter',
  templateUrl: 'rmt-internal-issue-reporter.html',
})
export class RmtInternalIssueReporterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('container', { read: ElementRef }) container;
  private static activeInstance: RmtInternalIssueReporterComponent = null;

  enabled = false;
  isOpen = false;
  isSending = false;
  categories = INTERNAL_ISSUE_CATEGORIES;
  severities = INTERNAL_ISSUE_SEVERITIES;
  category: InternalIssueCategory = InternalIssueCategory.Bug;
  severity: InternalIssueSeverity = InternalIssueSeverity.Medium;
  additionalText = '';
  maxCharacters = INTERNAL_ISSUE_TEXT_MAX_LENGTH;

  constructor(
    private reportProvider: InternalIssueReportProvider,
    private events: Events,
  ) { }

  // Lets the hardware back handler close the sheet before any page gets the press.
  static dismissIfOpen(): boolean {
    const instance = RmtInternalIssueReporterComponent.activeInstance;
    if (!instance || !instance.isOpen)
      return false;
    instance.close();
    return true;
  }

  ngOnInit() {
    RmtInternalIssueReporterComponent.activeInstance = this;
    this.refreshEnabled();
    this.events.subscribe(EVENTS_NAME.FEATURE_FLAGS_UPDATED, this.onFeatureFlagsUpdated);
  }

  ngAfterViewInit() {
    // same hoisting trick as rmt-snackbar: move out of app.html so it is not covered by pages
    const element = this.container.nativeElement;
    element.parentNode.removeChild(element);
    document.getElementsByTagName('ion-app')[0].appendChild(element);
  }

  ngOnDestroy() {
    this.events.unsubscribe(EVENTS_NAME.FEATURE_FLAGS_UPDATED, this.onFeatureFlagsUpdated);
    if (RmtInternalIssueReporterComponent.activeInstance === this)
      RmtInternalIssueReporterComponent.activeInstance = null;
  }

  async refreshEnabled() {
    try {
      this.enabled = await this.reportProvider.isEnabled();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to resolve internal issue reporter flag');
      this.enabled = false;
    }
  }

  open() {
    this.isOpen = true;
  }

  close() {
    if (this.isSending)
      return;
    this.isOpen = false;
  }

  selectCategory(category: InternalIssueCategory) {
    this.category = category;
  }

  selectSeverity(severity: InternalIssueSeverity) {
    this.severity = severity;
  }

  severityCssClass(severity: InternalIssueSeverity): string {
    return `severity-${severity.toLowerCase()}`;
  }

  async submit() {
    if (this.isSending)
      return;
    this.isSending = true;
    try {
      await this.reportProvider.send({
        category: this.category,
        severity: this.severity,
        additionalText: this.additionalText,
      });
      this.isSending = false;
      this.resetForm();
      this.close();
      RmtSnackbarComponent.present('Report sent. Thanks!');
    } catch (ex) {
      this.isSending = false;
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to send internal issue report');
      RmtSnackbarComponent.present('Could not send the report. Try again.', 'error');
    }
  }

  private resetForm() {
    this.category = InternalIssueCategory.Bug;
    this.severity = InternalIssueSeverity.Medium;
    this.additionalText = '';
  }

  private onFeatureFlagsUpdated = () => {
    this.refreshEnabled();
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-internal-issue-reporter/rmt-internal-issue-reporter.ts